import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import {
  TextField,
  Button,
  DialogActions,
  DialogContent,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { observer, useLocalObservable } from 'mobx-react-lite';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { action, autorun } from 'mobx';
import { ProfileData } from '../../store/UserStore';
import { getIdToken } from '../../firebaseConnection/authentication';
import { getUser, updateUser } from '../../http/requests';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string | undefined;
  disabled: boolean | undefined;
}
const UserDetailsDialog = observer(
  ({
    open,
    profileData,
    onClose,
    onSave,
  }: {
    open: boolean;
    profileData: ProfileData | null;
    onClose: () => void;
    onSave: () => void;
  }): JSX.Element => {
    const formData = useLocalObservable<FormData>(() => ({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      disabled: false,
    }));

    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      autorun(() => {
        (async function fetchUser() {
          if (profileData?.userId) {
            setLoading(true);
            try {
              const user = await getUser(profileData.userId);

              if (user) {
                formData.firstName = profileData?.firstName || '';
                formData.lastName = profileData?.lastName || '';
                formData.email = user.email || '';
                formData.password = '';
                formData.disabled = user.disabled;
              }
            } catch (e) {
              console.log(e);
            } finally {
              setLoading(false);
            }
          }
        })();
      });
    }, [profileData]);

    const handleClose = () => {
      onClose();
      formData.firstName = '';
      formData.lastName = '';
      formData.email = '';
      formData.password = '';
      formData.disabled = false;
      setErrorMsg(null);
    };

    const handleSave = () => {
      onSave();
      formData.firstName = '';
      formData.lastName = '';
      formData.email = '';
      formData.password = '';
      formData.disabled = false;
      setErrorMsg(null);
    };

    const saveEntry = async () => {
      try {
        setLoading(true);
        const idToken = await getIdToken();
        if (!idToken) throw new Error('Nicht eingeloggt?');
        if (!profileData?.userId) throw new Error('Fehlende UserID?');
        await updateUser(idToken, profileData?.userId, {
          email: formData.email,
          password: formData.password || undefined,
          disabled: formData.disabled,
        });
        handleSave();
      } catch (error) {
        if (error instanceof AxiosError) {
          switch (error?.response?.status) {
            case 400:
              setErrorMsg(
                'Passwort zu kurz (min 6 Zeichen) oder fehlerhafte Email-Adresse',
              );
              break;
            case 403:
              setErrorMsg('Keine Berechtigung zum Speichern der Nutzerdaten');
              break;
            case 401:
              setErrorMsg('Nicht eingeloggt?');
              break;
            default:
              setErrorMsg('Fehler beim Speichern der Nutzerdaten');
              break;
          }
        }
      } finally {
        setLoading(false);
      }
    };

    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>User Profil</DialogTitle>

        <DialogContent>
          <FormControlLabel
            control={
              <Switch
                checked={!formData.disabled}
                onChange={action(() => {
                  formData.disabled = !formData.disabled;
                })}
              />
            }
            label="Aktiv"
          />
          <TextField
            sx={{ marginTop: 2, marginBottom: 2 }}
            id="firstName"
            label="Vorname"
            margin="dense"
            fullWidth
            disabled={loading}
            variant="standard"
            value={formData.firstName}
            onChange={action(e => {
              formData.firstName = e.target.value;
            })}
          />
          <TextField
            sx={{ marginTop: 2, marginBottom: 2 }}
            id="lastName"
            label="Nachname"
            margin="dense"
            fullWidth
            disabled={loading}
            variant="standard"
            value={formData.lastName}
            onChange={action(e => {
              formData.lastName = e.target.value;
            })}
          />
          <TextField
            sx={{ marginTop: 2, marginBottom: 2 }}
            id="email"
            label="E-Mail"
            margin="dense"
            fullWidth
            disabled={loading}
            variant="standard"
            value={formData.email}
            onChange={action(e => {
              formData.email = e.target.value;
            })}
          />
          <TextField
            sx={{ marginTop: 2, marginBottom: 2 }}
            id="password"
            label="Passwort"
            margin="dense"
            fullWidth
            disabled={loading}
            variant="standard"
            value={formData.password}
            onChange={action(e => {
              formData.password = e.target.value;
            })}
          />
          <TextField
            sx={{ marginTop: 2, marginBottom: 2 }}
            id="address"
            label="Adresse"
            margin="dense"
            fullWidth
            disabled
            variant="standard"
            value={`${profileData?.addressStreet} ${profileData?.addressNumber} ${profileData?.addressZip} ${profileData?.addressCity}`}
          />
          <TextField
            sx={{ marginTop: 2, marginBottom: 2 }}
            id="phonw"
            label="Telefonnummer"
            margin="dense"
            fullWidth
            disabled
            variant="standard"
            value={`${profileData?.phone}`}
          />
          {profileData?.companyName && (
            <TextField
              sx={{ marginTop: 2, marginBottom: 2 }}
              id="company"
              label="Firma"
              margin="dense"
              fullWidth
              variant="standard"
              disabled
              value={`${profileData?.companyName} - ${profileData?.companyUid}`}
            />
          )}
          {profileData?.pointsStore.groupPoints && (
            <TextField
              sx={{ marginTop: 2, marginBottom: 2 }}
              id="company"
              label="Gesamtpunkte"
              margin="dense"
              fullWidth
              variant="standard"
              disabled
              value={profileData?.pointsStore.groupPoints}
            />
          )}
        </DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}
        <DialogActions>
          <Button disabled={loading} onClick={handleClose}>
            Abbrechen
          </Button>
          <Button disabled={loading} onClick={saveEntry}>
            Speichern
          </Button>
        </DialogActions>
        <Box p={1}>
          <Typography variant="body1">{errorMsg}</Typography>
        </Box>
      </Dialog>
    );
  },
);

export default UserDetailsDialog;
